/**
 * Screen grouping strategies for ScreenGridPanel.
 *
 * A grouper maps a screen to a `{ key, label }` section (or `null` to leave it
 * ungrouped). The grid renders one collapsible section per distinct key, in
 * first-seen order. The seam is intentionally generic so grouping can later be
 * driven by node `tags` (see `groupByTag`) once theme tags exist.
 */

import type { StepNode } from '@segmently/schema-models/v2';

export interface ScreenGroup {
  key: string;
  label: string;
}

export type ScreenGrouper = (screen: StepNode) => ScreenGroup | null;

/**
 * Functional category per StepKind. Kept local (not imported from reactFlow's
 * v2ScreenTypeConfig) to avoid a themes→reactFlow dependency cycle. The 13
 * StepKinds are stable; an unknown kind falls back to "Other".
 */
const STEP_KIND_CATEGORY: Record<string, ScreenGroup> = {
  HeroContent: { key: 'presentation', label: 'Presentation' },
  Carousel: { key: 'presentation', label: 'Presentation' },
  ProgressSteps: { key: 'presentation', label: 'Presentation' },
  ListSinglePick: { key: 'single-selection', label: 'Single selection' },
  GridSinglePick: { key: 'single-selection', label: 'Single selection' },
  WheelPicker: { key: 'single-selection', label: 'Single selection' },
  ListMultiPick: { key: 'multiple-selection', label: 'Multiple selection' },
  GridMultiPick: { key: 'multiple-selection', label: 'Multiple selection' },
  HeroList: { key: 'multiple-selection', label: 'Multiple selection' },
  TextInput: { key: 'input', label: 'Input' },
  Paywall: { key: 'commerce', label: 'Commerce' },
  WebEmbed: { key: 'custom', label: 'Custom' },
  FlexibleLayout: { key: 'custom', label: 'Custom' },
};

const OTHER_GROUP: ScreenGroup = { key: 'other', label: 'Other' };

/** Group screens by functional StepKind category (default for the picker). */
export const groupByStepKindCategory: ScreenGrouper = (screen) => {
  const kind = screen.screenType as string;
  return STEP_KIND_CATEGORY[kind] ?? OTHER_GROUP;
};

const UNTAGGED_GROUP: ScreenGroup = { key: '__untagged', label: 'Untagged' };

/**
 * Group screens by their first `tags` entry. Tag-ready for future theme tags
 * (nodes already carry a `tags: string[]`). Screens with no tag fall into
 * "Untagged".
 */
export const groupByTag: ScreenGrouper = (screen) => {
  const tags = (screen as unknown as { tags?: unknown }).tags;
  const first = Array.isArray(tags)
    ? tags.find((t): t is string => typeof t === 'string' && t.trim().length > 0)
    : undefined;
  if (!first) return UNTAGGED_GROUP;
  return { key: `tag:${first}`, label: first };
};
